import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Clock, 
  Quote, 
  Sparkles, 
  ChevronLeft, 
  ChevronRight, 
  RotateCcw, 
  Copy, 
  Check, 
  Calendar,
  Shuffle
} from 'lucide-react';
import { Client, CostCenter, Folio, PreInvoice, MeetMeeting } from '../types.ts';
import { getQuoteForDate, MOTIVATIONAL_QUOTES, MotivationalQuote } from '../data/motivationalQuotes.ts';
import { CertificatesExpirationBanner } from './CertificatesExpirationBanner.tsx';
import { 
  loadAllCertificatesFromStorage, 
  evaluateCertificatesAlerts, 
  CertificatesAlertEvaluation 
} from '../utils/certificateAlertHelper.ts';

interface WelcomePageProps {
  currentUser: {
    email: string;
    name: string;
    role: string;
    clientCode?: string;
  };
  folios: Folio[];
  preInvoices: PreInvoice[];
  meetings: MeetMeeting[];
  exchangeRate: number;
  onNavigate: (tab: string, sub?: string) => void;
  certAlertEvaluation?: CertificatesAlertEvaluation;
}

export default function WelcomePage({
  currentUser,
  onNavigate,
  certAlertEvaluation: initialCertEvaluation
}: WelcomePageProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dayOffset, setDayOffset] = useState(0);
  const [randomQuote, setRandomQuote] = useState<MotivationalQuote | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Dynamic real-time certificate alerts
  const [certAlertEval, setCertAlertEval] = useState<CertificatesAlertEvaluation>(() => {
    if (initialCertEvaluation) return initialCertEvaluation;
    const certs = loadAllCertificatesFromStorage();
    return evaluateCertificatesAlerts(certs);
  });

  useEffect(() => {
    if (initialCertEvaluation) {
      setCertAlertEval(initialCertEvaluation);
    }
  }, [initialCertEvaluation]);

  useEffect(() => {
    const refresh = () => {
      const certs = loadAllCertificatesFromStorage();
      setCertAlertEval(evaluateCertificatesAlerts(certs));
    };
    window.addEventListener('snf_certificates_updated', refresh);
    window.addEventListener('storage', refresh);
    refresh();
    return () => {
      window.removeEventListener('snf_certificates_updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate current date based on day offset
  const displayDate = new Date();
  displayDate.setDate(displayDate.getDate() + dayOffset);

  // Determine which quote to show
  const currentQuote = randomQuote || getQuoteForDate(displayDate);

  // Calculate day of the year
  const startOfYear = new Date(displayDate.getFullYear(), 0, 0);
  const diff = displayDate.getTime() - startOfYear.getTime() + (startOfYear.getTimezoneOffset() - displayDate.getTimezoneOffset()) * 60 * 1000;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));

  // Greetings based on time of day
  const getGreeting = () => {
    const hrs = currentTime.getHours();
    if (hrs < 12) return '¡Buenos días';
    if (hrs < 19) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  const handleCopyQuote = () => {
    const textToCopy = `"${currentQuote.quote}" — ${currentQuote.author}`;
    navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleRandomQuote = () => {
    const randomIndex = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
    setRandomQuote(MOTIVATIONAL_QUOTES[randomIndex]);
  };

  const handleResetToToday = () => {
    setDayOffset(0);
    setRandomQuote(null);
  };

  return (
    <div id="welcome-page-container" className="space-y-8 max-w-5xl mx-auto py-2">
      
      {/* Real-time Expiration Alert Banner */}
      {certAlertEval.hasUrgentAlerts && (
        <CertificatesExpirationBanner
          evaluation={certAlertEval}
          onNavigateToCertificates={() => onNavigate('internal_submenu', 'seguridad')}
        />
      )}

      {/* 1. ETIQUETA / ENCABEZADO DE BUENOS DÍAS */}
      <motion.div 
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#2e302e] border border-[#52554e] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden shadow-lg"
      >
        {/* Decorative background glow */}
        <div className="absolute right-0 top-0 w-80 h-80 bg-brand-red/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-brand-red text-white text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-0.5 rounded-full font-mono shadow-xs">
              SESIÓN OPERATIVA ACTIVA
            </span>
            <span className="text-xs text-[#bababa] font-mono">
              SNF • Servicios Nuevos De Filtración Hispanomexicános
            </span>
          </div>
          
          <h1 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">
            {getGreeting()}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-[#e2e2e2] to-[#bc1100]">{currentUser.name}</span>!
          </h1>
          
          <p className="text-xs text-[#c9c9c9] max-w-xl leading-relaxed">
            Te deseamos una excelente y productiva jornada laboral en la gestión logística y operativa de plantas.
          </p>
        </div>

        <div className="flex items-center gap-4 bg-[#242624] border border-[#52554e] p-4 rounded-xl min-w-[210px] shrink-0 shadow-inner">
          <Clock className="h-6 w-6 text-brand-red shrink-0 animate-pulse" />
          <div className="font-mono">
            <p className="text-[10px] text-[#989898] uppercase font-bold tracking-widest">Hora Local del Servidor</p>
            <p className="text-base font-bold text-white">
              {currentTime.toLocaleTimeString('es-MX', { hour12: false })}
            </p>
            <p className="text-[10px] text-[#bababa] mt-0.5">
              {currentTime.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
      </motion.div>

      {/* 2. SECCIÓN DE FRASES MOTIVACIONALES (UNA POR CADA DÍA DEL AÑO) */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-[#242624] border-2 border-[#52554e] rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl space-y-8"
      >
        {/* Subtle decorative quote watermark */}
        <Quote className="absolute -right-8 -bottom-8 w-64 h-64 text-[#2e302e]/60 pointer-events-none select-none rotate-12" />
        <Quote className="absolute -left-6 -top-6 w-32 h-32 text-brand-red/10 pointer-events-none select-none -rotate-12" />

        {/* Header bar of quote box */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#3a3d3a] pb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-brand-red/15 text-brand-red rounded-xl border border-brand-red/30">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <span className="text-[10px] font-mono font-bold text-brand-red uppercase tracking-widest block">
                INSPIRACIÓN Y MOTIVACIÓN DIARIA
              </span>
              <h2 className="text-lg font-bold text-white font-display flex items-center gap-2">
                Frase Motivacional del Día
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 font-mono text-xs bg-[#2e302e] border border-[#52554e] px-3.5 py-1.5 rounded-full text-slate-300 shadow-inner">
            <Calendar className="h-3.5 w-3.5 text-brand-red" />
            <span>Día {dayOfYear} de 365</span>
            <span className="text-[#52554e]">•</span>
            <span className="text-amber-400 font-bold">{currentQuote.category || 'Reflexión'}</span>
          </div>
        </div>

        {/* Central Quote Card Body */}
        <div className="my-6 text-center space-y-6 max-w-3xl mx-auto py-4">
          <div className="inline-block p-3 bg-brand-red/10 rounded-full border border-brand-red/20 mb-2">
            <Quote className="h-8 w-8 text-brand-red mx-auto" />
          </div>

          <blockquote className="text-xl md:text-2xl lg:text-3xl font-display font-medium text-white leading-relaxed tracking-wide italic">
            "{currentQuote.quote}"
          </blockquote>

          <div className="pt-2 flex flex-col items-center justify-center gap-1">
            <span className="h-0.5 w-12 bg-brand-red rounded-full"></span>
            <cite className="text-base md:text-lg font-bold text-amber-300 font-sans not-italic tracking-wide mt-2">
              — {currentQuote.author}
            </cite>
          </div>
        </div>

        {/* Interactive Control Footer */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-[#3a3d3a]">
          
          {/* Day Navigation */}
          <div className="flex items-center gap-1 bg-[#2e302e] border border-[#52554e] p-1 rounded-xl">
            <button
              onClick={() => { setRandomQuote(null); setDayOffset(prev => prev - 1); }}
              className="p-2 hover:bg-[#3a3d3a] text-slate-300 hover:text-white rounded-lg transition-all cursor-pointer flex items-center gap-1 text-xs font-mono"
              title="Día anterior"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Ayer</span>
            </button>

            <button
              onClick={handleResetToToday}
              className={`px-3 py-1.5 text-xs font-bold font-mono rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                dayOffset === 0 && !randomQuote 
                  ? 'bg-brand-red text-white shadow-xs' 
                  : 'text-slate-300 hover:bg-[#3a3d3a] hover:text-white'
              }`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Hoy</span>
            </button>

            <button
              onClick={() => { setRandomQuote(null); setDayOffset(prev => prev + 1); }}
              className="p-2 hover:bg-[#3a3d3a] text-slate-300 hover:text-white rounded-lg transition-all cursor-pointer flex items-center gap-1 text-xs font-mono"
              title="Día siguiente"
            >
              <span className="hidden sm:inline">Mañana</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRandomQuote}
              className="px-3.5 py-2 bg-[#2e302e] hover:bg-[#3a3d3a] border border-[#52554e] text-slate-200 hover:text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-xs"
              title="Cargar otra frase motivacional al azar"
            >
              <Shuffle className="h-3.5 w-3.5 text-amber-400" />
              <span>Frase Aleatoria</span>
            </button>

            <button
              onClick={handleCopyQuote}
              className={`px-4 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-xs border ${
                isCopied
                  ? 'bg-emerald-600 text-white border-emerald-500'
                  : 'bg-brand-red hover:bg-brand-red-hover text-white border-brand-red'
              }`}
            >
              {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              <span>{isCopied ? '¡Copiada!' : 'Copiar Frase'}</span>
            </button>
          </div>

        </div>
      </motion.div>

    </div>
  );
}
